const EventEmitter = require('events');

import { type } from "os";
import "regenerator-runtime/runtime";

const serialOptions = {
    baudRate: 115200,
    dataBits: 8,
    stopBits: 1,
    parity: 'none',
    bufferSize: 16384,
    flowControl: 'none',
}

export default class WebSerialPort extends EventEmitter {

    __port;
    __reader;
    __writer;
    __keepReading;

    constructor() {
        super();
        this.browserSupport = "serial" in navigator;
        this.__port = null;
        this.__reader = null;
        this.__writer = null;
        this.__keepReading = false;
        this.isOpen = false;
    }

    /**
     * @param {SerialPort} port
     */
    set port(port) {
        this.__port = port;
    }

    async requestSerialPort(filters = {}) {
        try {
            this.__port = await navigator.serial.requestPort(filters);
            return true;
        }
        catch (err) {
            console.error(`Cannot select serial port. error: ${err.message}.`);
            return false;
        }
    }

    async getAuthorizedPorts() {
        return await navigator.serial.getPorts();
    }

    getPortInfo() {
        if(!this.__port === null)
            throw new Error('Cannot get port information. Port not selected.');

        return this.__port.getInfo();
    }

    async openPort(options = serialOptions) {
        try {

            Object.keys(serialOptions).forEach(key => {
                if(options[key] == null)
                    options[key] = serialOptions[key];
            })

            await this.__port.open(options);
            this.isOpen = true;

            this.__reader = this.__port.readable.getReader();
            this.__writer = this.__port.writable.getWriter();

            this._readPort();
            return this.isOpen;
        }
        catch (e) {
            this.isOpen = false;
            this.__reader = null;
            this.__writer = null;

            throw new Error(`Cannot open port. error: ${e.message}`)
        }
    }

    async closePort() {
        try {
            this.__keepReading = false;
            await this.__reader.cancel();
            this.__writer.releaseLock();

            await this.__port.close();
            this.isOpen = false;
            this.__reader = null;
            this.__writer = null;
        }
        catch (e) {
            throw new Error(`Cannot close port. error: ${e.message}`)
        }
    }

    async writeData(data) {
        try {
            await this.__writer.write(data);
        }
        catch (e) {
            throw new Error(`Cannot write port. error: ${e.message}`);
        }
    }

    async _readPort() {
        this.__keepReading = true;
        let data;

        while (this.__port.readable && this.__keepReading) {
            try {
                while (true) {
                    const { value, done } = await this.__reader.read();
                    if (done) {
                        break;
                    }

                    if(value.length < 2 && (value[0] === 0x06 || value[0] === 0x15)) {
                        this.emit('data', value);
                    }
                    else {
                        if(typeof data === "undefined" || data === null)
                            data = value;
                        else
                            data = this._concatTypedArrays(data, value);

                        if(data !== null && data[data.length -2] === 0x03) {
                            this.emit('data', data);
                            data = null;
                        }
                    }
                }
            }
            catch (e) {
                if(typeof this._rejectPending == 'function') this._rejectPending(`Cannot read port. error: ${e.message}`);
                throw new Error(`Cannot read port. error: ${e.message}`);
            }
            finally {
                this.__reader.releaseLock();
            }
        }
    }

    _concatTypedArrays(a, b) {
        if(typeof a !== typeof b)
            throw new Error('Arrays must be of the same type');

        var c = new (a.constructor)(a.length + b.length);
        c.set(a, 0);
        c.set(b, a.length);
        return c;
    }
}